/**
 * Create Client Page
 *
 * This page provides a comprehensive form for creating new clients including:
 * - Client information (name, contact details, subscription tier)
 * - Admin user setup (name, email)
 * - Form validation and error handling
 * - Success feedback and navigation
 */

import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { ActivQMSLoader } from "../../components/ui/activqms-loader";
import { CREATE_CLIENT, LIST_CLIENTS } from "../../services/client-api";

interface CreateClientFormData {
  clientName: string;
  contactEmail: string;
  contactPhone: string;
  subscriptionTier: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
}

const initialFormData: CreateClientFormData = {
  clientName: "",
  contactEmail: "",
  contactPhone: "",
  subscriptionTier: "BASIC",
  adminFirstName: "",
  adminLastName: "",
  adminEmail: "",
};

export function CreateClientPage() {
  const navigate = useNavigate();
  const [formData, setFormData] =
    useState<CreateClientFormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<CreateClientFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionInProgress = useRef(false);

  const [createClient] = useMutation(CREATE_CLIENT, {
    refetchQueries: [{ query: LIST_CLIENTS }],
    onCompleted: (data) => {
      console.log("Client created successfully:", data);
      setIsSubmitting(false); // Reset state before navigation
      submissionInProgress.current = false;
      navigate("/clients", {
        state: {
          message: `Client "${data.createClient.clientName}" created successfully!`,
          type: "success",
        },
      });
    },
    onError: (error) => {
      console.error("Error creating client:", error);
      setIsSubmitting(false);
      submissionInProgress.current = false;
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Partial<CreateClientFormData> = {};

    // Required field validation
    if (!formData.clientName.trim()) {
      newErrors.clientName = "Client name is required";
    }

    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = "Contact email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = "Please enter a valid email address";
    }

    if (!formData.adminFirstName.trim()) {
      newErrors.adminFirstName = "Admin first name is required";
    }

    if (!formData.adminLastName.trim()) {
      newErrors.adminLastName = "Admin last name is required";
    }

    if (!formData.adminEmail.trim()) {
      newErrors.adminEmail = "Admin email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      newErrors.adminEmail = "Please enter a valid email address";
    }

    if (!formData.subscriptionTier) {
      newErrors.subscriptionTier = "Subscription tier is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof CreateClientFormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double submission using both state and ref
    if (isSubmitting || submissionInProgress.current) {
      console.log(
        "Form submission already in progress, ignoring duplicate submission"
      );
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    submissionInProgress.current = true;

    try {
      await createClient({
        variables: {
          input: formData,
        },
      });
      // Note: setIsSubmitting(false) and submissionInProgress.current = false
      // are handled in onCompleted/onError callbacks
    } catch (error) {
      console.error("Submit error:", error);
      setIsSubmitting(false);
      submissionInProgress.current = false;
    }
  };

  const subscriptionTiers = [
    { value: "FREE", label: "Free", description: "2 users, 1GB storage" },
    { value: "BASIC", label: "Basic", description: "10 users, 10GB storage" },
    {
      value: "PROFESSIONAL",
      label: "Professional",
      description: "50 users, 100GB storage",
    },
    {
      value: "ENTERPRISE",
      label: "Enterprise",
      description: "Unlimited users and storage",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate("/clients")}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          Back to Clients
        </button>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Create New Client</h1>
        <p className="mt-1 text-sm text-gray-600">
          Set up a new client account with admin user access
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Client Information */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Client Information
            </h3>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="clientName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Client Name *
                </label>
                <input
                  type="text"
                  id="clientName"
                  value={formData.clientName}
                  onChange={(e) =>
                    handleInputChange("clientName", e.target.value)
                  }
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.clientName ? "border-red-300" : ""
                  }`}
                  placeholder="Enter client name"
                />
                {errors.clientName && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.clientName}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="subscriptionTier"
                  className="block text-sm font-medium text-gray-700"
                >
                  Subscription Tier *
                </label>
                <select
                  id="subscriptionTier"
                  value={formData.subscriptionTier}
                  onChange={(e) =>
                    handleInputChange("subscriptionTier", e.target.value)
                  }
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.subscriptionTier ? "border-red-300" : ""
                  }`}
                >
                  {subscriptionTiers.map((tier) => (
                    <option key={tier.value} value={tier.value}>
                      {tier.label} - {tier.description}
                    </option>
                  ))}
                </select>
                {errors.subscriptionTier && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.subscriptionTier}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="contactEmail"
                  className="block text-sm font-medium text-gray-700"
                >
                  Contact Email *
                </label>
                <input
                  type="email"
                  id="contactEmail"
                  value={formData.contactEmail}
                  onChange={(e) =>
                    handleInputChange("contactEmail", e.target.value)
                  }
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.contactEmail ? "border-red-300" : ""
                  }`}
                  placeholder="contact@company.com"
                />
                {errors.contactEmail && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.contactEmail}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="contactPhone"
                  className="block text-sm font-medium text-gray-700"
                >
                  Contact Phone
                </label>
                <input
                  type="tel"
                  id="contactPhone"
                  value={formData.contactPhone}
                  onChange={(e) =>
                    handleInputChange("contactPhone", e.target.value)
                  }
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Admin User Setup */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Admin User Setup
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              This user will have administrative access to the client account
              and will receive login credentials via email.
            </p>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="adminFirstName"
                  className="block text-sm font-medium text-gray-700"
                >
                  First Name *
                </label>
                <input
                  type="text"
                  id="adminFirstName"
                  value={formData.adminFirstName}
                  onChange={(e) =>
                    handleInputChange("adminFirstName", e.target.value)
                  }
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.adminFirstName ? "border-red-300" : ""
                  }`}
                  placeholder="John"
                />
                {errors.adminFirstName && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.adminFirstName}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="adminLastName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Last Name *
                </label>
                <input
                  type="text"
                  id="adminLastName"
                  value={formData.adminLastName}
                  onChange={(e) =>
                    handleInputChange("adminLastName", e.target.value)
                  }
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.adminLastName ? "border-red-300" : ""
                  }`}
                  placeholder="Doe"
                />
                {errors.adminLastName && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.adminLastName}
                  </p>
                )}
              </div>

              <div className="sm:col-span-2">
                <label
                  htmlFor="adminEmail"
                  className="block text-sm font-medium text-gray-700"
                >
                  Admin Email *
                </label>
                <input
                  type="email"
                  id="adminEmail"
                  value={formData.adminEmail}
                  onChange={(e) =>
                    handleInputChange("adminEmail", e.target.value)
                  }
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.adminEmail ? "border-red-300" : ""
                  }`}
                  placeholder="admin@company.com"
                />
                {errors.adminEmail && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.adminEmail}
                  </p>
                )}
                <p className="mt-2 text-sm text-gray-500">
                  The admin user will receive login credentials at this email
                  address.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate("/clients")}
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <ActivQMSLoader size="sm" className="mr-2" />
                Creating Client...
              </>
            ) : (
              "Create Client"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
